import { InputLabel, Select, TextField } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../app/hooks";
import { RootState } from "../../app/store";
import { setValidationErrors } from "../../app/store/app/appSlice";
import {
  getDebtLists,
  getMatterDetails,
  putDefinables,
} from "../../app/store/matter/matterSlice";
import {
  ListCodeDesc,
  ProcurementAreas,
} from "../../shared/dto/common-list.dto";
import {
  IMatterDetailsDto,
  MatterLegalAidObj,
} from "../../shared/dto/matter-details.dto";
import { useAppInit } from "../../shared/hooks/use-app-init";
import {
  close,
  onBeforeClose,
  setWindowTitle,
} from "../../shared/utils/sdk-utils";
import {
  getMatterRequiredFields,
  validate,
} from "../../shared/utils/validation-utils";
import LocalDatePicker from "../components/date-picker";
import Footer from "../components/footer";
import MatterComponent from "../components/matter-component";
import TopBar from "../components/topbar";
import UnsavedDataDialog from "../components/unsaved-data-dialog";
import { getFilteredProcurementAreas } from "../../shared/utils/matter-utils";
import CustomCheckbox from "../components/custom-checkbox";

const DebtMatterDetails: FC = () => {
  const listsInitialised = useRef(false);
  const matterDetailsInitialised = useRef(false);
  const closeHandlerRegistered = useRef(false);
  const initialMatterDetails = useRef<IMatterDetailsDto | undefined>(undefined);
  const matterRef = useRef<IMatterDetailsDto | undefined>(undefined);

  const dispatch = useAppDispatch();

  const matterDetails = useSelector(
    (state: RootState) => state.matter.matterDetails
  );
  const matterLists = useSelector(
    (state: RootState) => state.matter.matterLists
  );
  const sdkApi = useSelector((state: RootState) => state.app.sdkApi);
  const saving = useSelector((state: RootState) => state.matter.saving);
  const sdkInitialised = useSelector(
    (state: RootState) => state.app.sdkInitialised
  );
  const appInit = useSelector((state: RootState) => state.app.appInit);

  const [currentMatterDetails, setCurrentMatterDetails] = useState<
    IMatterDetailsDto | undefined
  >(undefined);
  const [procurementAreaList, setProcurementAreaList] = useState<
    ProcurementAreas[] | undefined
  >(undefined);
  const [accessPoints, setAccessPoints] = useState<ListCodeDesc[] | undefined>(
    undefined
  );
  const [showUnsavedData, setShowUnsavedData] = useState(false);

  const onCancel = useCallback(() => {
    if (
      JSON.stringify(matterRef.current) !==
      JSON.stringify(initialMatterDetails.current)
    ) {
      setShowUnsavedData(true);
    } else {
      close(sdkApi, false);
    }
  }, [sdkApi]);

  useEffect(() => {
    matterRef.current = currentMatterDetails;
  }, [currentMatterDetails]);

  const getData = useCallback(() => {
    if (!listsInitialised.current) {
      listsInitialised.current = true;
      dispatch(getMatterDetails(true))
        .unwrap()
        .then(() => {
          dispatch(getDebtLists());
        })
        .catch(() => {});
    }
  }, [dispatch]);

  useAppInit(() => {
    getData();
  });

  useEffect(() => {
    if (!!sdkApi && !!sdkInitialised && !!appInit) {
      getData();
    }
  }, [dispatch, sdkApi, sdkInitialised, getData, appInit]);

  useEffect(() => {
    if (!!matterDetails && !!matterLists && !matterDetailsInitialised.current) {
      matterDetailsInitialised.current = true;
      setCurrentMatterDetails(matterDetails);
      initialMatterDetails.current = matterDetails;
      const group = matterLists?.procurementAreaList.find(
        (a) => a.code === matterDetails.legalAidObj?.procurementArea
      );
      if (group && group.accessPoints) {
        setAccessPoints(group.accessPoints);
      }
      getFilteredProcurementAreas(
        matterDetails,
        setCurrentMatterDetails,
        setProcurementAreaList,
        setAccessPoints,
        matterDetails.legalAidObj?.ufnDate,
        matterLists?.procurementAreaList
      );
      setWindowTitle(
        `${matterDetails?.matterReference || ""} ${
          matterDetails?.matterDescription || ""
        }`,
        sdkApi,
        60,
        "- Legal Aid Details"
      );
    }
  }, [matterDetails, matterLists, matterDetailsInitialised, sdkApi]);

  useEffect(() => {
    if (!closeHandlerRegistered.current && !!sdkApi) {
      closeHandlerRegistered.current = true;
      onBeforeClose(() => onCancel(), sdkApi);
    }
  }, [sdkApi, onCancel]);

  return (
    <>
      <TopBar helpUrl="https://community.leap.co.uk/s/article/Legal-Aid-Civil-Invoicing-a-Family-Controlled-Work-Claim" />
      <div className="main">
        <div className="mainsection">
          <div className="halfWidth">
            <MatterComponent
              matterDetails={currentMatterDetails}
              onUpdate={(tempMatterDetails) => {
                getFilteredProcurementAreas(
                  tempMatterDetails,
                  (matterDetails) => setCurrentMatterDetails(matterDetails),
                  (procurementAreaList) =>
                    setProcurementAreaList(procurementAreaList),
                  (accessPoints) => setAccessPoints(accessPoints),
                  tempMatterDetails?.legalAidObj?.ufnDate,
                  matterLists?.procurementAreaList
                );
              }}
              getLists={(tableNo) => {
                dispatch(getDebtLists(tableNo));
              }}
              rateList={matterLists?.rateList}
            />

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>LA Order No.</InputLabel>
              </div>
              <div className="flex7">
                <TextField
                  variant="outlined"
                  value={currentMatterDetails?.legalAidObj?.laOrderNumber || ""}
                  placeholder="NNNNNNNNNNNN"
                  inputProps={{ maxLength: 12 }}
                  onChange={(e) => {
                    setCurrentMatterDetails({
                      ...currentMatterDetails,
                      legalAidObj: {
                        ...currentMatterDetails?.legalAidObj,
                        laOrderNumber: e.target.value,
                      } as MatterLegalAidObj,
                    });
                  }}
                  fullWidth
                />
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>LA Order Date</InputLabel>
              </div>
              <div className="flex7">
                <LocalDatePicker
                  value={
                    currentMatterDetails?.legalAidObj?.laOrderDate
                      ? currentMatterDetails?.legalAidObj?.laOrderDate
                      : null
                  }
                  onUpdate={(value) => {
                    if (
                      value !== currentMatterDetails?.legalAidObj?.laOrderDate
                    ) {
                      const newValue = value;

                      if (newValue !== "Invalid date") {
                        setCurrentMatterDetails({
                          ...currentMatterDetails,
                          legalAidObj: {
                            ...currentMatterDetails?.legalAidObj,
                            laOrderDate: newValue,
                          } as MatterLegalAidObj,
                        });
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>Court</InputLabel>
              </div>
              <div className="flex7">
                <TextField
                  variant="outlined"
                  value={currentMatterDetails?.legalAidObj?.court || ""}
                  onChange={(e) => {
                    setCurrentMatterDetails({
                      ...currentMatterDetails,
                      legalAidObj: {
                        ...currentMatterDetails?.legalAidObj,
                        court: e.target.value,
                      } as MatterLegalAidObj,
                    });
                  }}
                  fullWidth
                />
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>Case No.</InputLabel>
              </div>
              <div className="flex7">
                <TextField
                  variant="outlined"
                  value={currentMatterDetails?.legalAidObj?.caseNumber || ""}
                  onChange={(e) => {
                    setCurrentMatterDetails({
                      ...currentMatterDetails,
                      legalAidObj: {
                        ...currentMatterDetails?.legalAidObj,
                        caseNumber: e.target.value,
                      } as MatterLegalAidObj,
                    });
                  }}
                  fullWidth
                />
              </div>
            </div>
          </div>
          <div className="halfWidthWithPadding">
            <div className="inputRow">
              <div className="flex4">
                <InputLabel>Procurement Area</InputLabel>
              </div>
              <div className="flex7">
                <div style={{ display: "grid" }}>
                  <Select
                    className="select"
                    native
                    fullWidth
                    variant="outlined"
                    value={
                      currentMatterDetails?.legalAidObj?.procurementArea || ""
                    }
                    onChange={(e) => {
                      const tempMatterDetails = {
                        ...currentMatterDetails,
                        legalAidObj: {
                          ...currentMatterDetails?.legalAidObj,
                          procurementArea: e.target.value,
                          accessPoint: "",
                        } as MatterLegalAidObj,
                      };
                      getFilteredProcurementAreas(
                        tempMatterDetails,
                        (matterDetails) =>
                          setCurrentMatterDetails(matterDetails),
                        (procurementAreaList) =>
                          setProcurementAreaList(procurementAreaList),
                        (accessPoints) => setAccessPoints(accessPoints),
                        tempMatterDetails?.legalAidObj?.ufnDate,
                        matterLists?.procurementAreaList
                      );
                    }}
                  >
                    <option key="" value="" className="emptyMenuItem"></option>
                    {(procurementAreaList || []).map((procurementArea) => (
                      <option
                        key={procurementArea.code}
                        value={procurementArea.code}
                      >
                        {`${procurementArea.code} ${procurementArea.description}`}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>Access Point</InputLabel>
              </div>
              <div className="flex7">
                <div style={{ display: "grid" }}>
                  <Select
                    fullWidth
                    native
                    variant="outlined"
                    value={currentMatterDetails?.legalAidObj?.accessPoint || ""}
                    onChange={(e) => {
                      setCurrentMatterDetails({
                        ...currentMatterDetails,
                        legalAidObj: {
                          ...currentMatterDetails?.legalAidObj,
                          accessPoint: e.target.value,
                        } as MatterLegalAidObj,
                      });
                    }}
                  >
                    <option key="" value="" className="emptyMenuItem"></option>
                    {accessPoints?.map((accessPoint) => (
                      <option
                        key={`${accessPoint.description} [${accessPoint.code}]`}
                        value={accessPoint.code}
                      >
                        {`${accessPoint.code} ${accessPoint.description}`}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>ECF Reference</InputLabel>
              </div>
              <div className="flex7">
                <TextField
                  variant="outlined"
                  value={currentMatterDetails?.legalAidObj?.ecfReference || ""}
                  placeholder="NNNNNNNAA"
                  inputProps={{ maxLength: 9 }}
                  onChange={(e) => {
                    setCurrentMatterDetails({
                      ...currentMatterDetails,
                      legalAidObj: {
                        ...currentMatterDetails?.legalAidObj,
                        ecfReference: e.target.value,
                      } as MatterLegalAidObj,
                    });
                  }}
                  fullWidth
                />
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>CLA Exemption Code</InputLabel>
              </div>
              <div className="flex7">
                <div style={{ display: "grid" }}>
                  <Select
                    fullWidth
                    variant="outlined"
                    native
                    value={
                      currentMatterDetails?.legalAidObj?.debt?.claExemption ||
                      ""
                    }
                    onChange={(e) => {
                      setCurrentMatterDetails({
                        ...currentMatterDetails,
                        legalAidObj: {
                          ...currentMatterDetails?.legalAidObj,
                          debt: {
                            ...currentMatterDetails?.legalAidObj?.debt,
                            claExemption: e.target.value,
                          },
                        } as MatterLegalAidObj,
                      });
                    }}
                  >
                    <option key="" value="" className="emptyMenuItem"></option>
                    {matterLists?.claExemptionCriteriaList?.map((caseStage) => (
                      <option
                        key={`${caseStage.description} [${caseStage.code}]`}
                        value={caseStage.code}
                      >
                        {`${caseStage.code} ${caseStage.description}`}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>CLA Reference</InputLabel>
              </div>
              <div className="flex7">
                <TextField
                  variant="outlined"
                  placeholder="ANNNNNN"
                  inputProps={{ maxLength: 7 }}
                  value={
                    currentMatterDetails?.legalAidObj?.debt?.claReference || ""
                  }
                  onChange={(e) => {
                    setCurrentMatterDetails({
                      ...currentMatterDetails,
                      legalAidObj: {
                        ...currentMatterDetails?.legalAidObj,
                        debt: {
                          ...currentMatterDetails?.legalAidObj?.debt,
                          claReference: e.target.value,
                        },
                      } as MatterLegalAidObj,
                    });
                  }}
                  fullWidth
                />
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>Schedule Reference</InputLabel>
              </div>
              <div className="flex7">
                <TextField
                  variant="outlined"
                  value={
                    currentMatterDetails?.legalAidObj?.scheduleReference || ""
                  }
                  placeholder="NANNNA/AAA/NN"
                  onChange={(e) => {
                    setCurrentMatterDetails({
                      ...currentMatterDetails,
                      legalAidObj: {
                        ...currentMatterDetails?.legalAidObj,
                        scheduleReference: e.target.value,
                      } as MatterLegalAidObj,
                    });
                  }}
                  fullWidth
                />
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <InputLabel>Transfer Date</InputLabel>
              </div>
              <div className="flex7">
                <LocalDatePicker
                  value={
                    currentMatterDetails?.legalAidObj?.transferDate
                      ? currentMatterDetails?.legalAidObj?.transferDate
                      : null
                  }
                  onUpdate={(value) => {
                    if (
                      value !== currentMatterDetails?.legalAidObj?.transferDate
                    ) {
                      const newValue = value;

                      if (newValue !== "Invalid date") {
                        setCurrentMatterDetails({
                          ...currentMatterDetails,
                          legalAidObj: {
                            ...currentMatterDetails?.legalAidObj,
                            transferDate: newValue,
                          } as MatterLegalAidObj,
                        });
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="inputRow">
              <div className="flex4">
                <CustomCheckbox
                  label="Early Legal Advice"
                  value={currentMatterDetails?.legalAidObj?.elaMatter}
                  onChange={(value) => {
                    setCurrentMatterDetails({
                      ...currentMatterDetails,
                      legalAidObj: {
                        ...currentMatterDetails?.legalAidObj,
                        elaMatter: value,
                      } as MatterLegalAidObj,
                    });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer
        onCancel={() => onCancel()}
        onSave={() => {
          const patternFields = [
            {
              value: currentMatterDetails?.legalAidObj?.laOrderNumber,
              label: "LA Order No.",
              pattern: /^(.{10}|.{12})$/,
            },
            {
              value: currentMatterDetails?.legalAidObj?.debt?.claReference,
              label: "CLA Reference",
              pattern: /^([A-Za-z]{1})\d{6}$/,
            },
          ];
          if (
            !!currentMatterDetails &&
            !validate(
              getMatterRequiredFields(currentMatterDetails),
              patternFields,
              (errors) => dispatch(setValidationErrors(errors))
            )?.length &&
            !saving
          ) {
            dispatch(putDefinables(currentMatterDetails)).then((action) => {
              if (action.meta.requestStatus !== "rejected") {
                close(sdkApi, false);
              }
            });
          }
        }}
      />
      <UnsavedDataDialog
        isOpen={showUnsavedData}
        onClose={() => setShowUnsavedData(false)}
      />
    </>
  );
};

export default DebtMatterDetails;
